import React, { useState, useRef, useEffect, useCallback } from 'react';
import { searchEntities, type ExploreSearchResult } from '../../lib/exploreApi';
import { findPaths, type FindPathsResult, type SinglePath } from '../../lib/graphApi';
import { NODE_COLORS } from './useForceGraph';
import { toast } from 'sonner';
import { PATH_REL_TYPES } from './GraphExplorerPage';

const CATEGORY_TO_LABEL: Record<string, string> = {
  actor: 'ThreatActor',
  family: 'MalwareFamily',
  ioc: 'IOC',
  cve: 'CVE',
  technique: 'AttackTechnique',
};

const CATEGORY_DISPLAY: Record<string, string> = {
  actor: 'Actor',
  family: 'Family',
  ioc: 'IOC',
  cve: 'CVE',
  technique: 'Technique',
};

interface SelectedEntity {
  name: string;
  label: string;
}

interface PathSearchPanelProps {
  onPathFound: (result: FindPathsResult) => void;
  loading: boolean;
}

function EntitySearchInput({
  placeholder,
  selected,
  onSelect,
  onClear,
  autoFocus,
}: {
  placeholder: string;
  selected: SelectedEntity | null;
  onSelect: (entity: SelectedEntity) => void;
  onClear: () => void;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ExploreSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    setSearching(true);
    try {
      const resp = await searchEntities({ query: q, limit: 10 });
      setResults(resp.results);
      setOpen(resp.results.length > 0);
      setActiveIndex(-1);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (selected) onClear();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 250);
  }, [doSearch, selected, onClear]);

  const handleSelect = useCallback((result: ExploreSearchResult) => {
    const label = CATEGORY_TO_LABEL[result.category] || 'MalwareFamily';
    setQuery(result.value);
    setOpen(false);
    onSelect({ name: result.value, label });
  }, [onSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < results.length) handleSelect(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }, [open, results, activeIndex, handleSelect]);

  return (
    <div className="graph-path-entity-input-wrap" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        className={`graph-path-search-input${selected ? ' selected' : ''}`}
        placeholder={placeholder}
        value={selected ? selected.name : query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (results.length > 0 && !selected) setOpen(true); }}
      />
      {searching && <span className="graph-search-bar-spinner" />}
      {selected && (
        <button className="graph-path-entity-clear" onClick={() => { onClear(); setQuery(''); }} title="Clear">
          &times;
        </button>
      )}
      {open && results.length > 0 && (
        <ul className="graph-path-search-dropdown">
          {results.map((r, i) => (
            <li
              key={`${r.category}-${r.value}`}
              className={`graph-path-search-item${i === activeIndex ? ' active' : ''}`}
              onMouseDown={() => handleSelect(r)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              <span className={`graph-search-bar-cat-badge graph-search-bar-cat-${r.category}`}>
                {CATEGORY_DISPLAY[r.category] || r.category}
              </span>
              <span className="graph-path-search-item-name">{r.value}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const MAX_ENTITIES = 5;

export function PathSearchPanel({ onPathFound, loading }: PathSearchPanelProps) {
  const [entities, setEntities] = useState<(SelectedEntity | null)[]>([null, null]);
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [relTypes, setRelTypes] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState<FindPathsResult | null>(null);

  const toggleRelType = useCallback((rt: string) => {
    setRelTypes(prev => prev.includes(rt) ? prev.filter(t => t !== rt) : [...prev, rt]);
  }, []);

  const setEntity = useCallback((index: number, entity: SelectedEntity | null) => {
    setEntities(prev => {
      const next = [...prev];
      next[index] = entity;
      return next;
    });
    if (!entity) setResult(null);
  }, []);

  const addWaypoint = useCallback(() => {
    setEntities(prev => prev.length < MAX_ENTITIES ? [...prev.slice(0, -1), null, prev[prev.length - 1]] : prev);
  }, []);

  const removeWaypoint = useCallback((index: number) => {
    setEntities(prev => prev.length > 2 ? prev.filter((_, i) => i !== index) : prev);
  }, []);

  const handleSubmit = useCallback(async () => {
    const filled = entities.filter((e): e is SelectedEntity => e !== null);
    if (filled.length < 2 || filled.length !== entities.length) return;
    setSearching(true);
    try {
      const pairs: [SelectedEntity, SelectedEntity][] = [];
      for (let i = 0; i < filled.length - 1; i++) {
        pairs.push([filled[i], filled[i + 1]]);
      }
      const results = await Promise.all(
        pairs.map(([s, t]) =>
          findPaths(
            '', s.label, s.name,
            '', t.label, t.name,
            5, 5, ['Chunk', 'ChunkRun'], true,
            relTypes.length ? relTypes : undefined,
            dateFrom || undefined,
            dateTo || undefined,
          )
        )
      );
      // Merge results from all pairs
      const merged: FindPathsResult = {
        paths: results.flatMap(r => r.paths),
        subgraph: {
          nodes: [],
          edges: [],
        },
        meta: {
          path_count: results.reduce((sum, r) => sum + r.meta.path_count, 0),
          shortest_length: Math.min(...results.map(r => r.meta.shortest_length)),
          used_fallback: results.some(r => r.meta.used_fallback),
          message: results.length === 1
            ? results[0].meta.message
            : `${results.reduce((s, r) => s + r.meta.path_count, 0)} paths across ${pairs.length} segments`,
        },
      };
      // Dedup nodes by id
      const nodeMap = new Map<string, (typeof merged.subgraph.nodes)[number]>();
      for (const r of results) {
        for (const n of r.subgraph.nodes) nodeMap.set(n.id, n);
      }
      merged.subgraph.nodes = [...nodeMap.values()];
      // Dedup edges by source+target+rel_type
      const edgeSet = new Set<string>();
      for (const r of results) {
        for (const e of r.subgraph.edges) {
          const key = `${e.source}|${e.target}|${e.rel_type}`;
          if (!edgeSet.has(key)) {
            edgeSet.add(key);
            merged.subgraph.edges.push(e);
          }
        }
      }
      setResult(merged);
      if (merged.paths.length > 0) {
        onPathFound(merged);
        toast.success(merged.meta.message);
      } else {
        toast('No paths found between these entities');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Path finding failed');
    } finally {
      setSearching(false);
    }
  }, [entities, relTypes, dateFrom, dateTo, onPathFound]);

  const canSubmit = entities.every(e => e !== null) && entities.length >= 2 && !searching && !loading;

  return (
    <div className="graph-path-search-panel">
      <div className="graph-path-search-panel-inputs">
        {entities.map((entity, i) => {
          const isFirst = i === 0;
          const isLast = i === entities.length - 1;
          const removable = !isFirst && !isLast;
          const placeholder = isFirst
            ? 'Entity 1...'
            : isLast
              ? `Entity ${i + 1}...`
              : 'Via entity...';
          return (
            <div key={i} className="graph-path-entity-row">
              <EntitySearchInput
                placeholder={placeholder}
                selected={entity}
                onSelect={e => setEntity(i, e)}
                onClear={() => setEntity(i, null)}
                autoFocus={isFirst}
              />
              {removable && (
                <button
                  className="graph-path-entity-remove"
                  onClick={() => removeWaypoint(i)}
                  title="Remove waypoint"
                >
                  &times;
                </button>
              )}
            </div>
          );
        })}
        {entities.length < MAX_ENTITIES && (
          <button className="graph-path-add-waypoint" onClick={addWaypoint}>
            + Add waypoint
          </button>
        )}
      </div>

      <div className="graph-path-filter-toggle">
        <button
          className="graph-path-filter-toggle-btn"
          onClick={() => setShowFilters(v => !v)}
        >
          Filters {showFilters ? '▾' : '▸'}
        </button>
      </div>

      {showFilters && (
        <div className="graph-path-filter-section">
          <div className="expansion-settings-section">
            <div className="expansion-settings-label">Date range</div>
            <div className="expansion-settings-date-row">
              <input
                type="date"
                className="expansion-settings-date-input"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
              />
              <span className="expansion-settings-date-sep">-</span>
              <input
                type="date"
                className="expansion-settings-date-input"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
              />
            </div>
          </div>
          <div className="expansion-settings-section">
            <div className="expansion-settings-label">
              Relationship types
              <button
                className="expansion-settings-all-btn"
                onClick={() => setRelTypes(prev => prev.length === PATH_REL_TYPES.length ? [] : PATH_REL_TYPES.map(t => t.value))}
              >
                {relTypes.length === PATH_REL_TYPES.length ? 'Reset' : 'All'}
              </button>
            </div>
            <div className="expansion-settings-type-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              {PATH_REL_TYPES.map(rt => (
                <label key={rt.value} className="expansion-settings-checkbox-label">
                  <input
                    type="checkbox"
                    checked={relTypes.includes(rt.value)}
                    onChange={() => toggleRelType(rt.value)}
                  />
                  {rt.display}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        className="graph-path-search-panel-submit"
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        {searching ? 'Searching...' : 'Find Paths'}
      </button>

      {result && result.paths.length > 0 && (
        <div className="graph-path-search-panel-results">
          <div className="graph-path-results-header" style={{ position: 'static' }}>
            <span>{result.meta.message}</span>
          </div>
          <div className="graph-path-results-list">
            {result.paths.map((p: SinglePath, idx: number) => (
              <button
                key={idx}
                className="graph-path-item"
                title={`Path ${idx + 1}: ${p.length} hop(s)`}
              >
                {p.nodes.map((step, si) => (
                  <React.Fragment key={si}>
                    <span
                      className="graph-path-node"
                      style={{ color: NODE_COLORS[step.node_label] ?? '#aaa' }}
                    >
                      {step.node_name.length > 20
                        ? step.node_name.slice(0, 20) + '...'
                        : step.node_name}
                    </span>
                    {si < p.edges.length && (
                      <span className="graph-path-arrow">
                        <span className="graph-path-rel">{p.edges[si]}</span>
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
